import { formatAppMoney } from './appConfigRuntime';

const API_ROOT = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/').replace(/\/?$/, '/');

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(tenantId && { 'X-Tenant-Id': tenantId }),
  };
};

const billingBase = `${API_ROOT}synctrack/billing`;

async function parseJson(res, fallbackMsg) {
  if (!res.ok) {
    let msg = fallbackMsg || `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.message) msg = body.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
}

export async function fetchBillingDashboard() {
  const res = await fetch(`${billingBase}/dashboard`, { headers: getAuthHeaders() });
  return parseJson(res, 'Failed to load billing dashboard');
}

export async function fetchBillingAccounts() {
  const res = await fetch(`${billingBase}/accounts`, { headers: getAuthHeaders() });
  const data = await parseJson(res, 'Failed to load billing accounts');
  return Array.isArray(data) ? data : [];
}

export async function fetchBillingAccountByTenant(tenantId) {
  const res = await fetch(`${billingBase}/tenants/${tenantId}/account`, {
    headers: getAuthHeaders(),
  });
  if (res.status === 404) return null;
  return parseJson(res, 'Failed to load billing account');
}

export async function upsertBillingAccount(tenantId, body) {
  const res = await fetch(`${billingBase}/tenants/${tenantId}/account`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to save billing account');
}

export async function fetchPricingPlans(includeInactive = true) {
  const qs = includeInactive ? '?includeInactive=true' : '';
  const res = await fetch(`${billingBase}/pricing-plans${qs}`, { headers: getAuthHeaders() });
  const data = await parseJson(res, 'Failed to load pricing plans');
  return Array.isArray(data) ? data : [];
}

export async function createPricingPlan(body) {
  const res = await fetch(`${billingBase}/pricing-plans`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to create pricing plan');
}

export async function updatePricingPlan(pricingPlanId, body) {
  const res = await fetch(`${billingBase}/pricing-plans/${pricingPlanId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to update pricing plan');
}

export async function upsertPricingItem(pricingPlanId, body) {
  const res = await fetch(`${billingBase}/pricing-plans/${pricingPlanId}/items`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to save pricing item');
}

export async function syncPricingPlanFromProductMaster(pricingPlanId) {
  const res = await fetch(
    `${billingBase}/pricing-plans/${pricingPlanId}/sync-from-product-master`,
    { method: 'POST', headers: getAuthHeaders() }
  );
  return parseJson(res, 'Failed to sync from Product Master');
}

export async function fetchTenantSubscriptions(tenantId) {
  const res = await fetch(`${billingBase}/tenants/${tenantId}/subscriptions`, {
    headers: getAuthHeaders(),
  });
  const data = await parseJson(res, 'Failed to load subscriptions');
  return Array.isArray(data) ? data : [];
}

export async function createBillingSubscription(tenantId, body) {
  const res = await fetch(`${billingBase}/tenants/${tenantId}/billing-subscriptions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to create billing subscription');
}

export async function setBillingSubscriptionStatus(billingSubscriptionId, status) {
  const res = await fetch(`${billingBase}/billing-subscriptions/${billingSubscriptionId}/status`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status }),
  });
  return parseJson(res, 'Failed to update billing subscription');
}

export async function fetchTenantCharges(tenantId) {
  const res = await fetch(`${billingBase}/tenants/${tenantId}/charges`, {
    headers: getAuthHeaders(),
  });
  const data = await parseJson(res, 'Failed to load charges');
  return Array.isArray(data) ? data : [];
}

export async function upsertSubscriptionCharge(body) {
  const res = await fetch(`${billingBase}/charges`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to save charge');
}

/** Mid-cycle limit upgrade: bills only the +delta; original charge quantity stays intact. */
export async function upgradeSubscriptionCharge(chargeId, additionalQuantity) {
  const res = await fetch(`${billingBase}/charges/${chargeId}/upgrade`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ additionalQuantity }),
  });
  return parseJson(res, 'Failed to upgrade limit');
}

export async function applyPricingPlanToSubscription(subscriptionId, pricingPlanId) {
  const res = await fetch(
    `${billingBase}/subscriptions/${subscriptionId}/apply-pricing-plan/${pricingPlanId}`,
    { method: 'POST', headers: getAuthHeaders() }
  );
  return parseJson(res, 'Failed to apply pricing plan');
}

/** Preview mid-term plan upgrade/downgrade (remaining months × rate delta). */
export async function previewPricingPlanChange(subscriptionId, pricingPlanId) {
  const res = await fetch(
    `${billingBase}/subscriptions/${subscriptionId}/change-pricing-plan/preview`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ pricingPlanId }),
    }
  );
  return parseJson(res, 'Failed to preview plan change');
}

/** Apply mid-term plan change: freeze originals, new rates for remainder, invoice or credit. */
export async function changePricingPlanMidTerm(subscriptionId, pricingPlanId) {
  const res = await fetch(
    `${billingBase}/subscriptions/${subscriptionId}/change-pricing-plan`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ pricingPlanId }),
    }
  );
  return parseJson(res, 'Failed to change pricing plan');
}

export async function fetchUsageBilling({ tenantId, status } = {}) {
  const qs = new URLSearchParams();
  if (tenantId) qs.set('tenantId', tenantId);
  if (status) qs.set('status', status);
  const res = await fetch(`${billingBase}/usage${qs.toString() ? `?${qs}` : ''}`, {
    headers: getAuthHeaders(),
  });
  const data = await parseJson(res, 'Failed to load usage');
  return Array.isArray(data) ? data : [];
}

export async function refreshUsageBilling(tenantId) {
  const res = await fetch(`${billingBase}/tenants/${tenantId}/usage/refresh`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return parseJson(res, 'Failed to refresh usage');
}

export async function fetchInvoices({ tenantId, status } = {}) {
  const qs = new URLSearchParams();
  if (tenantId) qs.set('tenantId', tenantId);
  if (status) qs.set('status', status);
  const res = await fetch(`${billingBase}/invoices${qs.toString() ? `?${qs}` : ''}`, {
    headers: getAuthHeaders(),
  });
  const data = await parseJson(res, 'Failed to load invoices');
  return Array.isArray(data) ? data : [];
}

export async function fetchInvoice(invoiceId) {
  const res = await fetch(`${billingBase}/invoices/${invoiceId}`, { headers: getAuthHeaders() });
  if (res.status === 404) return null;
  return parseJson(res, 'Failed to load invoice');
}

export async function generateInvoice(tenantId, body = {}) {
  const res = await fetch(`${billingBase}/tenants/${tenantId}/invoices/generate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to generate invoice');
}

export async function transitionInvoice(invoiceId, action, reason = '') {
  const res = await fetch(`${billingBase}/invoices/${invoiceId}/${action}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  });
  return parseJson(res, `Failed to ${action} invoice`);
}

export async function fetchPayments(tenantId) {
  const qs = tenantId ? `?tenantId=${tenantId}` : '';
  const res = await fetch(`${billingBase}/payments${qs}`, { headers: getAuthHeaders() });
  const data = await parseJson(res, 'Failed to load payments');
  return Array.isArray(data) ? data : [];
}

export async function recordPayment(body) {
  const res = await fetch(`${billingBase}/payments`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to record payment');
}

export async function fetchAdjustments(tenantId) {
  const qs = tenantId ? `?tenantId=${tenantId}` : '';
  const res = await fetch(`${billingBase}/adjustments${qs}`, { headers: getAuthHeaders() });
  const data = await parseJson(res, 'Failed to load adjustments');
  return Array.isArray(data) ? data : [];
}

export async function createAdjustment(body) {
  const res = await fetch(`${billingBase}/adjustments`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to create adjustment');
}

export async function fetchRenewals() {
  const res = await fetch(`${billingBase}/renewals`, { headers: getAuthHeaders() });
  const data = await parseJson(res, 'Failed to load renewals');
  return Array.isArray(data) ? data : [];
}

export async function fetchBillingConfiguration() {
  const res = await fetch(`${billingBase}/configuration`, { headers: getAuthHeaders() });
  return parseJson(res, 'Failed to load billing configuration');
}

export async function fetchProductMaster() {
  const res = await fetch(`${billingBase}/product-master`, { headers: getAuthHeaders() });
  const data = await parseJson(res, 'Failed to load product master');
  return Array.isArray(data) ? data : [];
}

/** Update Product Type (Subscription / Per-unit / Fixed) and unit on a catalog sub-product. */
export async function updateProductComponent(componentId, body) {
  const res = await fetch(`${billingBase}/product-master/components/${componentId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(body || {}),
  });
  return parseJson(res, 'Failed to update product component');
}

export async function fetchTenantProductGrid(tenantId, subscriptionId) {
  const qs = subscriptionId ? `?subscriptionId=${subscriptionId}` : '';
  const res = await fetch(`${billingBase}/tenants/${tenantId}/product-grid${qs}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res, 'Failed to load product cost grid');
}

export async function fetchTenantBillingSummary(tenantId) {
  const res = await fetch(`${billingBase}/tenants/${tenantId}/summary`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res, 'Failed to load billing summary');
}

export const formatMoney = (v, currency) => formatAppMoney(v, currency);

export async function fetchBillingNotifications(limit = 50) {
  const res = await fetch(`${billingBase}/notifications?limit=${limit}`, { headers: getAuthHeaders() });
  const data = await parseJson(res, 'Failed to load notifications');
  return Array.isArray(data) ? data : [];
}

export async function fetchNotificationSettings() {
  const res = await fetch(`${billingBase}/notifications/settings`, { headers: getAuthHeaders() });
  return parseJson(res, 'Failed to load notification settings');
}

export async function saveNotificationSettings(body) {
  const res = await fetch(`${billingBase}/notifications/settings`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to save notification settings');
}

export async function runBillingNotifications() {
  const res = await fetch(`${billingBase}/notifications/run`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return parseJson(res, 'Failed to run notifications');
}

export async function setSubscriptionStatus(subscriptionId, status) {
  const res = await fetch(`${billingBase}/subscriptions/${subscriptionId}/status`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status }),
  });
  return parseJson(res, 'Failed to update subscription');
}

export async function paymentAction(paymentId, action) {
  const res = await fetch(`${billingBase}/payments/${paymentId}/${action}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return parseJson(res, `Failed to ${action} payment`);
}

export async function fetchTaxRules() {
  const res = await fetch(`${billingBase}/tax-rules`, { headers: getAuthHeaders() });
  const data = await parseJson(res, 'Failed to load tax rules');
  return Array.isArray(data) ? data : [];
}

export async function saveTaxRule(body) {
  const res = await fetch(`${billingBase}/tax-rules`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to save tax rule');
}

export async function fetchIntegration() {
  const res = await fetch(`${billingBase}/integration`, { headers: getAuthHeaders() });
  return parseJson(res, 'Failed to load integration settings');
}

export async function saveIntegration(body) {
  const res = await fetch(`${billingBase}/integration`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to save integration settings');
}

export async function fetchBillingPermissions() {
  const res = await fetch(`${billingBase}/permissions`, { headers: getAuthHeaders() });
  const data = await parseJson(res, 'Failed to load permissions');
  return Array.isArray(data) ? data : [];
}

export async function setBillingPermission(code, enabled) {
  const res = await fetch(`${billingBase}/permissions/${code}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ enabled }),
  });
  return parseJson(res, 'Failed to update permission');
}

export async function proposeRenewal(body) {
  const res = await fetch(`${billingBase}/renewals`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseJson(res, 'Failed to propose renewal');
}

export async function renewalAction(renewalId, action) {
  const res = await fetch(`${billingBase}/renewals/${renewalId}/${action}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return parseJson(res, `Failed to ${action} renewal`);
}

export async function fetchBillingReport(kind, tenantId) {
  const qs = tenantId ? `?tenantId=${tenantId}` : '';
  const res = await fetch(`${billingBase}/reports/${kind}${qs}`, { headers: getAuthHeaders() });
  return parseJson(res, 'Failed to load report');
}

export async function downloadInvoicesCsv() {
  const res = await fetch(`${billingBase}/export/invoices.csv`, {
    headers: { ...getAuthHeaders(), Accept: 'text/csv, */*' },
  });
  if (!res.ok) throw new Error('Failed to export invoices');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'invoices.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadInvoicePdf(invoiceId) {
  const res = await fetch(`${billingBase}/invoices/${invoiceId}/pdf`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to download PDF');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice-${invoiceId}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function sendInvoice(invoiceId) {
  const res = await fetch(`${billingBase}/invoices/${invoiceId}/send`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return parseJson(res, 'Failed to send invoice');
}

export const pctUsed = (used, included) => {
  const u = Number(used);
  const i = Number(included);
  if (!Number.isFinite(u) || !Number.isFinite(i) || i <= 0) return '—';
  return `${Math.round((u / i) * 100)}%`;
};

/** Spec §4 CDX billing menu — only these items. */
export const BILLING_NAV = [
  { label: 'Dashboard', path: '/admin/billing' },
  { label: 'Billing Accounts', path: '/admin/billing/accounts' },
  { label: 'Pricing & Plans', path: '/admin/billing/pricing' },
  { label: 'Subscriptions', path: '/admin/billing/subscriptions' },
  { label: 'Usage & Overage', path: '/admin/billing/usage' },
  { label: 'Invoices', path: '/admin/billing/invoices' },
  { label: 'Payments', path: '/admin/billing/payments' },
  { label: 'Credits & Adjustments', path: '/admin/billing/credits' },
  { label: 'Renewals', path: '/admin/billing/renewals' },
  { label: 'Reports', path: '/admin/billing/reports' },
  { label: 'Billing Configuration', path: '/admin/billing/config' },
];
